import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import type { ApiError } from '../api';
import { usePermissions } from '../session';
import { Button, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import { INPUT } from '../pages/event/shared';

/**
 * STGD's per-ticket platform fee (D-391, D-394), in the STGD section.
 *
 * The owner's shape: a fee per ticket, no caps, from a date. Each change is
 * appended, never edited, so the table below is the whole history. A change
 * takes effect today or later; entering another for the same date replaces
 * it. **Nothing is collected yet** (plan P6): this is the schedule the
 * collection will read.
 */

export interface PlatformFeeScheduleView {
  today: string;
  current: { amount_cents: number; effective_on: string } | null;
  upcoming: { amount_cents: number; effective_on: string }[];
  collecting: false;
}

export interface PlatformFeeRateRow {
  id: string;
  amount_cents: number;
  effective_on: string;
  note: string | null;
  created_by: string | null;
  created_at: string;
}

export const platformFeeEndpoints = {
  get: () => api.get<{ schedule: PlatformFeeScheduleView; history: PlatformFeeRateRow[] }>('/api/admin/platform/fees'),
  set: (input: { amount_cents: number; effective_on: string; note: string | null }) => api.post<{ schedule: PlatformFeeScheduleView; history: PlatformFeeRateRow[] }>('/api/admin/platform/fees', input),
};

export const dollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export function PlatformFeeSchedulePanel() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const fees = useQuery({ queryKey: ['platform', 'fees'], queryFn: platformFeeEndpoints.get });
  const [amount, setAmount] = useState('');
  const [effectiveOn, setEffectiveOn] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => platformFeeEndpoints.set({ amount_cents: Math.round(Number(amount) * 100), effective_on: effectiveOn, note: note.trim() || null }),
    onSuccess: () => {
      setError(null);
      setAmount('');
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['platform', 'fees'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const schedule = fees.data?.schedule;
  const valid = amount.trim() !== '' && Number.isFinite(Number(amount)) && Number(amount) >= 0 && /^\d{4}-\d{2}-\d{2}$/.test(effectiveOn);

  return (
    <Panel title="STGD platform fee" description="STGD’s fee on each ticket sold by card: web, box office card, keyed card, payment links and installments. No fee on cash, check, house accounts, account credit, comps, $0 tickets or gifts. Not collected yet.">
      <div className="space-y-4 px-5 py-4">
        {fees.error ? <ErrorNote>{(fees.error as ApiError).message}</ErrorNote> : null}
        {error ? <ErrorNote>{error}</ErrorNote> : null}
        <div className="text-sm text-ink-900">
          <p>
            <span className="font-semibold">In effect today ({schedule?.today ?? '…'}): </span>
            {schedule?.current ? `${dollars(schedule.current.amount_cents)} per ticket, since ${schedule.current.effective_on}` : 'no fee'}
          </p>
          {(schedule?.upcoming ?? []).map((change) => (
            <p key={change.effective_on} className="text-ink-600">
              From {change.effective_on}: {change.amount_cents === 0 ? 'no fee' : `${dollars(change.amount_cents)} per ticket`}
            </p>
          ))}
        </div>

        {can('platform.fees.manage') ? (
          <form
            className="grid grid-cols-1 gap-3 sm:grid-cols-[10rem_12rem_1fr_auto] sm:items-end"
            onSubmit={(event) => {
              event.preventDefault();
              if (valid) save.mutate();
            }}
          >
            <Field label="Fee per ticket ($)" htmlFor="fee-amount">
              <input id="fee-amount" className={`${INPUT} w-full`} inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="0.50" />
            </Field>
            <Field label="From" htmlFor="fee-from" hint="Today or later">
              <input id="fee-from" type="date" className={`${INPUT} w-full`} min={schedule?.today} value={effectiveOn} onChange={(event) => setEffectiveOn(event.target.value)} />
            </Field>
            <Field label="Why" htmlFor="fee-note">
              <input id="fee-note" className={`${INPUT} w-full`} value={note} onChange={(event) => setNote(event.target.value)} placeholder="For the history" />
            </Field>
            <Button type="submit" variant="primary" disabled={!valid || save.isPending}>
              {save.isPending ? 'Saving…' : 'Schedule'}
            </Button>
          </form>
        ) : null}

        <Table headers={['From', 'Per ticket', 'Why', 'Entered']}>
          {(fees.data?.history ?? []).map((row) => (
            <tr key={row.id}>
              <td className="px-4 py-2 text-sm">{row.effective_on}</td>
              <td className="px-4 py-2 text-sm">{dollars(row.amount_cents)}</td>
              <td className="px-4 py-2 text-sm">{row.note ?? '—'}</td>
              <td className="px-4 py-2 text-xs text-ink-600">{formatWhen(row.created_at)}{row.created_by ? ` by ${row.created_by}` : ''}</td>
            </tr>
          ))}
        </Table>
      </div>
    </Panel>
  );
}
